import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { getTempleByQrCode } from "@/lib/yatra-db";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { QrCode, Camera, X } from "lucide-react";
import { jainArt } from "@/components/jain-icon";

export const Route = createFileRoute("/app/scan")({ component: ScanScreen });

function ScanScreen() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const handleCode = async (code: string) => {
    const trimmed = code.trim().toUpperCase();
    const data = getTempleByQrCode(trimmed);
    if (!data) {
      return toast.error("Invalid QR code: " + trimmed);
    }
    toast.success("Found: " + data.name_en);
    nav({ to: "/app/temples/$id", params: { id: data.id } });
  };

  const start = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const html5 = new Html5Qrcode("qr-region");
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            await html5.stop().catch(() => {});
            scannerRef.current = null;
            setScanning(false);
            handleCode(decoded);
          },
          () => {},
        );
      } catch {
        setScanning(false);
        toast.error("Camera unavailable. Use manual code.");
      }
    }, 50);
  };

  const stop = async () => {
    if (scannerRef.current) {
      await scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(
    () => () => {
      scannerRef.current?.stop().catch(() => {});
    },
    [],
  );

  return (
    <div className="px-5 pt-16">
      <div className="mb-4">
        <span className="font-serif text-2xl font-bold text-gradient">{t("scanQr")}</span>
        <div className="text-[11px] text-muted-foreground">Scan to unlock temple history</div>
      </div>

      {scanning ? (
        <div className="space-y-3">
          <div className="relative overflow-hidden rounded-2xl border-2 border-saffron bg-black">
            <div id="qr-region" />
            {/* corner brackets */}
            <div className="pointer-events-none absolute inset-6 rounded-xl border-2 border-saffron/0">
              <div className="absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-gold" />
              <div className="absolute right-0 top-0 h-6 w-6 border-r-2 border-t-2 border-gold" />
              <div className="absolute bottom-0 left-0 h-6 w-6 border-b-2 border-l-2 border-gold" />
              <div className="absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-gold" />
            </div>
          </div>
          <button onClick={stop} className="btn-outline w-full">
            <X className="h-4 w-4" /> Stop
          </button>
        </div>
      ) : (
        <div
          className="relative overflow-hidden rounded-2xl border-2 border-dashed bg-card p-6 text-center"
          style={{ borderColor: "hsl(var(--saffron))" }}
        >
          <img
            src={jainArt.mandala}
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 opacity-10 animate-spin-slow"
          />
          <div
            className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-2xl ring-glow"
            style={{ background: "var(--gradient-saffron)" }}
          >
            <QrCode className="h-10 w-10 text-white" />
          </div>
          <div className="mt-4 font-serif text-base font-bold">Scan Temple QR Code</div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            Har derasar par ek unique QR hai. Scan karo aur uski itihaas padho.
          </div>
          <button onClick={start} className="btn-primary mt-4 w-full">
            <Camera className="h-4 w-4" /> Open Camera
          </button>
        </div>
      )}

      <div className="mt-5 rounded-2xl border border-border bg-card p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Or enter code manually
        </div>
        <div className="mt-2 flex gap-2">
          <input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            placeholder="JGY-T01"
            className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <button
            onClick={() => manualCode && handleCode(manualCode)}
            className="btn-primary py-2 text-xs"
          >
            Go
          </button>
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground">Format: JGY-T01 to JGY-T31</div>
      </div>
    </div>
  );
}
